import type { CSSProperties } from "react"

export type HorizontalNineSliceOptions = {
  /** Image URL (SVG/PNG). Wrap in quotes when embedded in CSS. */
  url: string
  /** Source px from the left edge (fixed cap in slice geometry). */
  sliceLeft: number
  /** Source px from the right edge. */
  sliceRight: number
  /** Rendered left cap width (defaults to `sliceLeft`). */
  capWidthLeft?: number
  /** Rendered right cap width (defaults to `sliceRight`). */
  capWidthRight?: number
}

function nineSliceShorthand({
  sliceLeft,
  sliceRight,
  capWidthLeft = sliceLeft,
  capWidthRight = sliceRight,
  fillCenter,
}: HorizontalNineSliceOptions & { fillCenter: boolean }) {
  const fill = fillCenter ? " fill" : ""
  return {
    slice: `0 ${sliceRight} 0 ${sliceLeft}${fill}`,
    width: `0 ${capWidthRight}px 0 ${capWidthLeft}px`,
    capWidthLeft,
    capWidthRight,
  }
}

/**
 * Horizontal 3-slice `border-image`: fixed end caps, stretch only the flat middle.
 * Same pattern as the balance pill (`InfoBlock`) and `TournamentBanner` frame.
 */
export function horizontalNineSliceStyle(
  options: HorizontalNineSliceOptions
): CSSProperties {
  const { url } = options
  const { slice, width } = nineSliceShorthand({ ...options, fillCenter: true })
  return {
    borderStyle: "solid",
    borderColor: "transparent",
    borderWidth: width,
    borderImageSource: `url("${url}")`,
    borderImageSlice: slice,
    borderImageWidth: width,
    borderImageRepeat: "stretch",
  }
}

/** Frame only (no stretched center fill) — pairs with {@link horizontalMaskBoxFillStyle}. */
export function horizontalNineSliceFrameStyle(
  options: HorizontalNineSliceOptions
): CSSProperties {
  const { url } = options
  const { slice, width } = nineSliceShorthand({ ...options, fillCenter: false })
  return {
    borderStyle: "solid",
    borderColor: "transparent",
    borderWidth: width,
    borderImageSource: `url("${url}")`,
    borderImageSlice: slice,
    borderImageWidth: width,
    borderImageRepeat: "stretch",
  }
}

/** Matches preset SVG radial fill (`#53536c` → `#39394c`). */
export const PRESET_BUTTON_FILL_GRADIENT =
  "radial-gradient(ellipse 90% 110% at 14% 22%, #53536c 0%, #39394c 100%)"

/**
 * Texture fill through a nine-sliced mask (TournamentBanner-style). Keeps chipped
 * end caps 1:1; only the flat middle grows — avoids pill `fill` stretch.
 */
export function horizontalMaskBoxFillStyle(
  options: HorizontalNineSliceOptions & {
    backgroundImage?: string
  }
): CSSProperties {
  const { url, backgroundImage = PRESET_BUTTON_FILL_GRADIENT } = options
  const { slice, width } = nineSliceShorthand({ ...options, fillCenter: true })
  const source = `url("${url}")`
  return {
    backgroundImage,
    WebkitMaskBoxImage: `${source} ${slice} / ${width} stretch`,
  }
}

/** Symmetric caps — only where the art is symmetric (e.g. simple pills). */
export function symmetricHorizontalNineSliceStyle(
  url: string,
  slicePx: number,
  capWidthPx = slicePx
): CSSProperties {
  return horizontalNineSliceStyle({
    url,
    sliceLeft: slicePx,
    sliceRight: slicePx,
    capWidthLeft: capWidthPx,
    capWidthRight: capWidthPx,
  })
}
